import {createContext, useContext, useEffect, useState} from "react";
import {FetchOptions, ModifyURL, useFetch} from "@/base/utils/fetch";
import {useRouter} from "next/router";
import {UserData} from "@/base/types/UserData";
import {compareObjects} from "@/base/utils/compare.util";
import {signIn, signOut, UserStoreProvider, useSession} from "@/utils/UserStore";
import {SessionData, SignInResponse} from "@/base/types/SessionData";
import {deleteCache, getCache, putCache} from "@/base/utils/cache";
import {UserRegionPrefers} from "@/base/types/User/UserRegionPrefers";
import {useMemoryState} from "@/base/utils/useMemoryState";
import {useMemoryEffect} from "@/base/components/util/useMemoryEffect";

type Context = {
	user: UserData | null;
	setUser: (user: any) => void;
	data: SessionData | any | null;
	Fetch: (opts: FetchOptions) => Promise<any>;
	logOut: () => Promise<any>;
	isSudo: () => Promise<boolean>;
	saveForSudo: (user?: UserData, session?: SessionData) => Promise<void>;
	exitFromSudo: () => Promise<void>;
	currency: string;
	router: any;
	loggedIn: boolean;
	isUserLoading: boolean;
	setIsUserLoading: (loading: boolean) => void;
	regionPrefer?: UserRegionPrefers;
	updateUser: (force?: boolean) => Promise<UserData | null>;
}

const UserContext = createContext({} as Context)

type UserProviderProps = {
	children: React.ReactNode;
	session?: SessionData;
	regionPrefer?: UserRegionPrefers;
	referer?: string | null;
	comeFrom?: string | null;
}

const logOut = (context: Context) => {
	context.setIsUserLoading(true);

	return new Promise(async (resolve, reject) => {
		await signOut().finally(async () => {
			await deleteCache('user_updated_at').catch(console.error);
			await deleteCache('original-user-auth').catch(console.error);
			await deleteCache('original-user').catch(console.error);

			setTimeout(async () => {
				context.setUser(null);
				context.setIsUserLoading(false);

				await context.updateUser(true).catch(console.error);

				setTimeout(async () => {
					// clear all cache
					window.location.href = '/';

					resolve(true);
				}, 100);
			}, 100);
		}).catch(err => {
			context.setUser(null);
			context.setIsUserLoading(false);

			reject(err);
		});
	});
}

const updateUser = async (context: Context, force: boolean = false): Promise<UserData | null> => {
	const userUpdatedAt = await getCache('user_updated_at', undefined, 60 * 1000);

	if (!force && userUpdatedAt && context.data?.access_token && context.user != null)
	{
		return context.user;
	}

	if (!context.data?.access_token)
	{
		context.setIsUserLoading(false);
		context.setUser(null);
		return context.user;
	}
	// if (context.data?.access_token && context.user != null && context.isFirstLoad)
	// {
	// 	return context.user;
	// }

	context.setIsUserLoading(true);

	await putCache('user_updated_at', new Date()).catch(err => {
		console.error(err);
	})

	return context.Fetch({
		url: ModifyURL(`/user/info`),
	})
		.then(async res => {
			return res?.data?.info || null
		})
		.then(user => {
			if (!compareObjects(user, context.user))
			{
				context.setUser(user as UserData);
			}
			return user as UserData;
		})
		.catch(async err => {
			if (err.redirect)
			{
				await logOut(context).catch((err2) => {
					console.error("Failed to log out", err2)
				});
			}
			else
			{
				console.error(err);
				console.debug("set user catch - " + null)
				context.setUser(null);
			}
			return null;
		})
		.finally(() => {
			context.setIsUserLoading(false);
		})
}

export const FallbackCurrency = "USD";

export const AUserProvider = ({children, session, referer, comeFrom, regionPrefer = undefined}: UserProviderProps) => {
	const dynamicSession = useSession();
	const [data, setData] = useMemoryState(session);

	useMemoryEffect(() => {
		if (dynamicSession.data && !compareObjects(dynamicSession.data, data))
		{
			setData(dynamicSession.data);
		}
	}, [dynamicSession.data]);

	const inUser = (data?.user as UserData) || null;
	const [user, setUser] = useState<UserData | null>(inUser);
	const [currency, setCurrency] = useMemoryState(inUser?.currency || regionPrefer?.currency || FallbackCurrency);
	const [loggedIn, setLoggedIn] = useMemoryState(data?.access_token != null);
	const [isUserLoading, setIsUserLoading] = useState(false);
	// const [isFirstLoad, setIsFirstLoad] = useState(true);
	const [Fetch] = useFetch(data, referer, comeFrom);
	const router = useRouter();

	useEffect(() => {
		setCurrency(user?.currency || regionPrefer?.currency || FallbackCurrency);
		console.debug("DX user currency for", currency, user?.id || "Anon");
		setLoggedIn(user != null && user?.id != null);
		console.debug("DX user logged in", loggedIn);
	}, [user]);

	const context: Context = {
		logOut: async () => logOut(context),
		Fetch: (opts: FetchOptions) => {
			return Fetch(opts)
				.then(async res => {
					if (res && res.data && res.data.logout && res.data.logout === true)
					{
						await logOut(context);
					}

					return res
				})
		},
		isSudo: async () => (((await getCache('original-user-auth')) as any)?.access_token || null) != null &&
			(((await getCache('original-user') as any))?.name || null) != null,
		saveForSudo: async (_user, _data) => {
			await putCache('original-user-auth', _data || data);
			await putCache('original-user', _user || user);
		},
		exitFromSudo: async () => {
			const token = ((await getCache('original-user-auth')) as any)?.access_token || null;
			await logOut(context);

			if (token != null)
			{
				await signIn(token).finally(() => {
					deleteCache('original-user-auth');
					deleteCache('original-user');
				});
			}
		},
		// isFirstLoad,
		// setIsFirstLoad,
		user,
		currency,
		loggedIn,
		setUser: (nUser: any) => {
			if (nUser == user)
			{
				console.debug('DX user is same')
				return;
			}

			console.log("[DX] Changing user to " + nUser?.name)

			setUser(nUser);
		},
		updateUser: async (force: boolean = true) => updateUser(context, force),
		data,
		isUserLoading,
		setIsUserLoading,
		regionPrefer,
		router
	}

	// detect data change
	useMemoryEffect(() => {
		console.log("[DX] Updating user...")
		updateUser(context);
	}, [data, router.locale])

	return (
		<UserContext.Provider value={context}>
			{children}
		</UserContext.Provider>
	)
}

export const UserProvider = ({children, session, comeFrom, referer, regionPrefer = undefined}: UserProviderProps) => {
	return (
		<UserStoreProvider session={session}>
			<AUserProvider session={session} referer={referer} comeFrom={comeFrom} regionPrefer={regionPrefer}>
				{children}
			</AUserProvider>
		</UserStoreProvider>
	)
}

export type UserContextOutput = Omit<Context, 'router' | 'logOut' | 'data' | 'setIsUserLoading'> & {
	session: SessionData | any | null;
	logout: () => Promise<any>;
	tryAuthToken: (token: string) => Promise<SignInResponse | undefined>;
	updateUser: (force?: boolean) => Promise<UserData | null>;
};

export const useUser = (): UserContextOutput => {
	const context = useContext(UserContext);

	if (context === undefined)
	{
		throw new Error('useUser must be used within a UserProvider')
	}

	return {
		user: context.user,
		isUserLoading: context.isUserLoading,
		loggedIn: context.loggedIn,
		session: context.data,
		Fetch: context.Fetch,
		logout: context.logOut,
		currency: context.currency,
		setUser: context.setUser,
		tryAuthToken: signIn,
		regionPrefer: context.regionPrefer,
		isSudo: context.isSudo,
		saveForSudo: context.saveForSudo,
		exitFromSudo: context.exitFromSudo,
		updateUser: async (force: boolean = true) => updateUser(context, force),
	};
}